/** Área de conteúdo do livro em PDF (Fase 10 TASK-10/11).
 *
 * Renderiza com pdf.js na origem da app (ADR-009): canvas para a imagem da
 * página e **camada de texto** por cima, transparente e selecionável. É a camada
 * de texto que dá seleção, TTS e marcação — os três itens que o ADR-009 chama de
 * acessibilidade mínima do formato PDF. Sem ela o aluno tem uma imagem que vira
 * de página.
 *
 * Clipping por `released_locators`: seção não liberada não desenha canvas NEM
 * monta camada de texto — `getTextContent` entrega o texto de qualquer página do
 * arquivo, então montar a camada numa seção bloqueada colocaria o conteúdo na
 * tela pela porta dos fundos.
 */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { SearchPanelContext } from './searchPanelContext'

import { HighlightLayer } from '@/features/reader/components/HighlightLayer'
import { HighlightMenu } from '@/features/reader/components/HighlightMenu'
import { OverlayDialog } from '@/features/reader/components/OverlayDialog'
import { ReadAloudButton } from '@/features/reader/components/ReadAloudButton'
import { Watermark } from '@/features/reader/components/Watermark'
import { ZoomControls } from '@/features/reader/components/ZoomControls'
import { SelectionNote } from '@/features/reader/components/SelectionNote'
import { PdfSearch } from '@/features/reader/components/PdfSearch'
import type { NovoDestaque } from '@/features/reader/hooks/useHighlights'
import { useDRM } from '@/features/reader/hooks/useDRM'
import { usePdfPageRender } from '@/features/reader/hooks/usePdfPageRender'
import { useScrollPosition } from '@/features/reader/hooks/useScrollPosition'
import { useSwipeNavigation } from '@/features/reader/hooks/useSwipeNavigation'
import { useTextSelection } from '@/features/reader/hooks/useTextSelection'
import {
  usePdfDocument,
  type PdfLoader,
  type TextLayerRenderer,
} from '@/features/reader/hooks/usePdfDocument'
import { useAccessibilityStore } from '@/features/reader/stores/accessibilityStore'
import { useReaderStore } from '@/features/reader/stores/readerStore'
import { fatorDeZoomDoTexto } from '@/features/reader/utils/preferences'
import { rolarPara, topoParaPct } from '@/features/reader/utils/scroll'
import { temRolagemHorizontal } from '@/features/reader/utils/swipe'
import { desenharWatermark, textoDaWatermark } from '@/features/reader/utils/watermark'
import { isPageReleased } from '@/features/reader/utils/toc'
import {
  centroDe,
  escalaDeCliqueAdaptativa,
  escalaParaRegiao,
  focoDoClique,
  limitarPeloOrcamento,
  offsetDeFoco,
} from '@/features/reader/utils/zoom'
import type { BookAccess, CorDestaque, Highlight, Overlay, ZoomRegion } from '@/types/api'

interface PDFContentAreaProps {
  access: BookAccess
  page: number
  onPageChange: (page: number) => void
  studentName: string
  loader?: PdfLoader
  textLayerRenderer?: TextLayerRenderer
  /** Chamado quando o storage recusa a URL, para disparar a renovação. */
  onFalhaDeCarregamento?: (erro: unknown) => void
  /** Renovação em voo: a tela diz "renovando" em vez de acusar erro que já está
   *  sendo resolvido. */
  renovando?: boolean
  /** Destaques desta página, já salvos. */
  destaques?: Highlight[]
  /** Criar destaque a partir da seleção; ausente desliga a marcação. */
  onCriarDestaque?: (entrada: {
    page: number
    rect: import('@/types/api').Rect
    rects: import('@/types/api').Rect[]
    text_content: string
    color: CorDestaque
    note?: string
    client_id?: string
  }) => void | Promise<unknown>
  /** Seam de teste para a seleção (jsdom não implementa getClientRects). */
  selecaoDeps?: Parameters<typeof useTextSelection>[0]
}

/** Largura de leitura confortável quando o container ainda não foi medido. */
const LARGURA_PADRAO = 800

export function PDFContentArea({
  access,
  page,
  onPageChange,
  studentName,
  loader,
  textLayerRenderer,
  onFalhaDeCarregamento,
  renovando = false,
  destaques = [],
  onCriarDestaque,
  selecaoDeps,
}: PDFContentAreaProps) {
  const searchTarget = useContext(SearchPanelContext)
  // Refs de callback em vez de `useRef`: os elementos nascem depois do primeiro
  // render (e só existem quando a página está liberada), e o hook de render
  // precisa reagir ao momento em que aparecem.
  const [wrapper, setWrapper] = useState<HTMLDivElement | null>(null)
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null)
  const [camadaTexto, setCamadaTexto] = useState<HTMLDivElement | null>(null)
  const [area, setArea] = useState<HTMLDivElement | null>(null)
  const [openedOverlay, setOpenedOverlay] = useState<Overlay | null>(null)
  const [noteTarget, setNoteTarget] = useState<NovoDestaque | null>(null)
  const [larguraDisponivel, setLarguraDisponivel] = useState(LARGURA_PADRAO)

  const { doc, numPages, error } = usePdfDocument({
    url: access.content_url,
    ...(loader ? { loader } : {}),
  })

  useDRM(true)

  const liberada = useMemo(() => isPageReleased(access, page), [access, page])
  const overlaysDaPagina = useMemo(
    () => access.overlays.filter((o) => o.page === page),
    [access.overlays, page],
  )
  const total = numPages || access.page_count || 0

  // A responsividade vem da MEDIDA do container, não de `max-width` no canvas:
  // encolher o canvas por CSS faria a caixa deixar de bater com o viewport, e a
  // camada de texto ficaria deslocada em toda tela estreita.
  useEffect(() => {
    if (!area) return
    const observador = new ResizeObserver((entradas) => {
      const largura = entradas[0]?.contentRect.width
      if (largura && largura > 0) setLarguraDisponivel(largura)
    })
    observador.observe(area)
    return () => observador.disconnect()
  }, [area])

  // O "tamanho do texto" da US-03 vira ZOOM aqui: num PDF a página é imagem
  // rasterizada, e a única forma de o texto crescer é a página crescer junto.
  // O painel de acessibilidade avisa isso na legenda do controle.
  const fatorDeTexto = useAccessibilityStore((s) => fatorDeZoomDoTexto(s.preferencias))

  const zoom = useReaderStore((s) => s.zoom)
  const foco = useReaderStore((s) => s.foco)
  const aplicarZoom = useReaderStore((s) => s.aplicarZoom)
  const limparFoco = useReaderStore((s) => s.limparFoco)
  const setScrollPct = useReaderStore((s) => s.setScrollPct)
  const restaurarScroll = useReaderStore((s) => s.restaurarScroll)
  const limparRestauracao = useReaderStore((s) => s.limparRestauracao)

  // Zoom manual MANDA sobre o tamanho de texto: a escala de uma região já foi
  // calculada para a região caber na tela, e multiplicá-la por 1,5 a faria
  // transbordar exatamente no caso em que o aluno pediu para enxergar melhor.
  const escalaFixa = zoom
  const fatorDeZoom = zoom === null ? fatorDeTexto : 1

  // Gravada NO canvas, depois do render: não há nó de DOM para remover pelo
  // inspetor, sobrevive a "salvar imagem" e acompanha o zoom de graça.
  const marca = textoDaWatermark(studentName)
  const gravarMarca = useCallback(
    (contexto: CanvasRenderingContext2D, m: { largura: number; altura: number; dpr: number }) => {
      desenharWatermark(contexto, {
        texto: marca,
        largura: m.largura,
        altura: m.altura,
        dpr: m.dpr,
      })
    },
    [marca],
  )

  const render = usePdfPageRender({
    doc,
    page,
    larguraDisponivel,
    escalaFixa,
    fatorDeZoom,
    wrapper,
    canvas,
    textContainer: camadaTexto,
    habilitado: liberada,
    aoDesenhar: gravarMarca,
    ...(textLayerRenderer ? { textLayerRenderer } : {}),
  })

  const regioesDaPagina = useMemo(
    () => access.zoom_regions.filter((r) => r.page === page),
    [access.zoom_regions, page],
  )

  // O container de rolagem é o pai: a página ampliada transborda a coluna de
  // leitura, e é ele que rola. Ir buscá-lo por `parentElement` acopla ao layout,
  // mas a alternativa (procurar ancestral rolável por `getComputedStyle`) é
  // intestável em jsdom, que não faz layout.
  const rolagem = area?.parentElement ?? null

  // ⚠️ `||` e não `??`: `clientWidth` vale ZERO — não `undefined` — enquanto o
  // elemento está oculto (aba em segundo plano, ancestral `display:none`) ou na
  // primeira passada de layout. Com `??`, o zero passaria adiante e o zoom por
  // região não faria nada, em silêncio.
  const areaVisivel = useCallback(
    () => ({
      width: rolagem?.clientWidth || larguraDisponivel,
      height: rolagem?.clientHeight || 0,
    }),
    [rolagem, larguraDisponivel],
  )

  const ampliarRegiao = useCallback(
    (regiao: ZoomRegion) => {
      if (!render.tamanho) return
      const natural = {
        width: render.tamanho.width / render.escala,
        height: render.tamanho.height / render.escala,
      }
      const alvo = escalaParaRegiao(regiao.rect, natural, areaVisivel())
      const centro = centroDe(regiao.rect)
      aplicarZoom(limitarPeloOrcamento(natural, alvo), {
        ...centro,
        aposVersao: render.versao,
      })
    },
    [render.tamanho, render.escala, render.versao, areaVisivel, aplicarZoom],
  )

  // Fallback da US-06: sem regiões definidas, o duplo clique amplia onde o aluno
  // clicou. Duplo e não simples — clique simples é seleção de texto.
  const ampliarNoClique = useCallback(
    (evento: React.MouseEvent<HTMLDivElement>) => {
      // Já ampliado, o duplo clique volta ao ajuste — é o par natural do gesto.
      // Vem ANTES das medidas de propósito: desfazer o zoom não precisa de
      // página desenhada, e exigir isso prenderia o aluno no zoom enquanto a
      // página não terminasse de renderizar.
      if (zoom !== null) {
        aplicarZoom(null)
        return
      }
      if (!wrapper || !render.tamanho) return
      const ponto = focoDoClique(evento, wrapper.getBoundingClientRect())
      if (!ponto) return
      const natural = {
        width: render.tamanho.width / render.escala,
        height: render.tamanho.height / render.escala,
      }
      const alvo = escalaDeCliqueAdaptativa(render.escala, natural, areaVisivel())
      aplicarZoom(limitarPeloOrcamento(natural, alvo), {
        ...ponto,
        aposVersao: render.versao,
      })
    },
    [wrapper, render.tamanho, render.escala, render.versao, areaVisivel, zoom, aplicarZoom],
  )

  // Arrastar na horizontal só vira página quando a página CABE na tela: quem
  // ampliou uma tabela precisa alcançar a coluna da direita, e virar a página
  // no lugar disso é a forma mais rápida de tornar o zoom inútil.
  useSwipeNavigation({
    alvo: wrapper,
    onAnterior: () => onPageChange(Math.max(1, page - 1)),
    onProxima: () => onPageChange(total > 0 ? Math.min(total, page + 1) : page + 1),
    habilitado: liberada,
    rolagemHorizontal: temRolagemHorizontal(
      render.tamanho?.width ?? 0,
      areaVisivel().width,
    ),
  })

  // Posição DENTRO da página (N16). Antes disto o `scroll_pct` era sempre 0: a
  // retomada acertava a página e errava o ponto dela — meia tela de diferença
  // numa página ampliada.
  useScrollPosition({ alvo: rolagem, onMudar: setScrollPct, habilitado: liberada })

  // Restauração da abertura: uma vez, e só depois de o render reportar que
  // terminou. Antes disso o container ainda não tem altura rolável e o destino
  // cai em zero — que é o mesmo que não restaurar, mas gastando a única chance.
  useEffect(() => {
    if (restaurarScroll === null || !rolagem) return
    if (render.status !== 'pronto' || render.versao === 0) return
    rolarPara(rolagem, {
      left: rolagem.scrollLeft,
      top: topoParaPct(restaurarScroll, rolagem.scrollHeight, rolagem.clientHeight),
    })
    limparRestauracao()
  }, [restaurarScroll, rolagem, render.status, render.versao, limparRestauracao])

  // A rolagem só faz sentido DEPOIS do render na NOVA escala: antes disso o
  // wrapper ainda tem a caixa antiga e o destino cai no lugar errado — foi
  // exatamente o que aconteceu, e o teste da região o pega.
  useEffect(() => {
    if (!foco || !rolagem || !render.tamanho) return
    if (render.status !== 'pronto' || render.versao <= foco.aposVersao) return
    const destino = offsetDeFoco(foco, render.tamanho, {
      width: rolagem.clientWidth,
      height: rolagem.clientHeight,
    })
    rolarPara(rolagem, destino)
    limparFoco()
  }, [foco, rolagem, render.tamanho, render.status, render.versao, limparFoco])

  const { selecao, limpar } = useTextSelection({
    chave: page,
    pagina: wrapper,
    habilitado: liberada && onCriarDestaque !== undefined,
    ...(selecaoDeps ?? {}),
  })

  const destaquesDaPagina = useMemo(
    () => destaques.filter((h) => h.page === page),
    [destaques, page],
  )

  // Caminho REATIVO da renovação: a URL pré-assinada venceu durante a leitura e
  // o storage devolveu 403.
  useEffect(() => {
    if (error) onFalhaDeCarregamento?.(error)
  }, [error, onFalhaDeCarregamento])

  if (error && renovando) {
    return (
      <p role="status" className="pdf-reconectando">
        Renovando o acesso ao livro…
      </p>
    )
  }

  if (error) {
    return (
      <div className="reader-error" role="alert">
        Não foi possível abrir o livro. Tente recarregar a página.
      </div>
    )
  }

  return (
    <div className="pdf-content" data-testid="pdf-content" ref={setArea}>
      {doc && searchTarget && createPortal(<PdfSearch key={`${access.content_url}:${access.full_access}:${access.released_locators.join(',')}`}
        doc={doc} access={access} onGo={onPageChange} />, searchTarget)}
      {!doc && <p role="status">Carregando o livro…</p>}

      {!liberada ? (
        <div className="pdf-locked" role="status" data-testid="pdf-locked">
          <p>Este capítulo ainda não foi liberado pelo seu professor.</p>
        </div>
      ) : (
        <div
          className="pdf-page-wrapper"
          data-testid="pdf-page-wrapper"
          ref={setWrapper}
          onDoubleClick={ampliarNoClique}
        >
          <canvas ref={setCanvas} data-testid="pdf-canvas" aria-label={`Página ${page}`} />

          {/* A classe `textLayer` é o contrato do pdf.js: o CSS dela posiciona
              os spans e os deixa transparentes. */}
          <div className="textLayer" data-testid="pdf-text-layer" ref={setCamadaTexto} />

          {/* Segunda camada, para o caso de o canvas não dar contexto 2D. A
              que vale está GRAVADA no canvas acima. DRM best-effort (ADR-009). */}
          <Watermark texto={marca} />

          <HighlightLayer destaques={destaquesDaPagina} />

          {selecao && onCriarDestaque && (
            <HighlightMenu
              ancora={selecao.ancora}
              onFechar={limpar}
              onAnotar={() => {
                setNoteTarget({ page, rect: selecao.envolvente, rects: selecao.rects,
                  text_content: selecao.texto, color: 'yellow', client_id: crypto.randomUUID() })
                limpar()
              }}
              onEscolher={async (cor) => {
                try { await onCriarDestaque({
                  page,
                  rect: selecao.envolvente,
                  rects: selecao.rects,
                  text_content: selecao.texto,
                  color: cor,
                }); limpar() } catch { /* O aviso global permite tentar novamente. */ }
              }}
            />
          )}

          <div className="pdf-overlay-layer">
            {regioesDaPagina.map((regiao, indice) => (
              <ZoomRegionButton
                key={`${regiao.page}-${indice}`}
                regiao={regiao}
                indice={indice}
                onAmpliar={() => ampliarRegiao(regiao)}
              />
            ))}
            {overlaysDaPagina.map((overlay) => (
              <OverlayMarker key={overlay.id} overlay={overlay} onOpen={() => setOpenedOverlay(overlay)} />
            ))}
          </div>

          {render.status === 'erro' && (
            <p role="alert">Não foi possível desenhar esta página.</p>
          )}
        </div>
      )}

      {noteTarget && onCriarDestaque && <SelectionNote key={noteTarget.client_id}
        target={noteTarget} onSave={onCriarDestaque} onClose={() => setNoteTarget(null)} />}
      <nav className="pdf-nav" aria-label="Navegação de páginas">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          aria-label="Página anterior"
          title="Página anterior (←)"
        >
          <span aria-hidden="true">‹</span>
        </button>

        <span data-testid="pdf-page-indicator" aria-live="polite">
          Página {page} de {total || '—'}
        </span>

        <button
          type="button"
          onClick={() => onPageChange(Math.min(total || page + 1, page + 1))}
          disabled={total > 0 && page >= total}
          aria-label="Próxima página"
          title="Próxima página (→)"
        >
          <span aria-hidden="true">›</span>
        </button>

        {/* Ferramentas da página à direita, separadas da navegação: ouvir e
            ampliar agem sobre a página atual, não mudam de página. */}
        {liberada && (
          <div className="pdf-nav__ferramentas">
            <ReadAloudButton
              camada={camadaTexto}
              page={page}
              pronto={render.status === 'pronto'}
            />
            {render.status === 'pronto' && (
              <ZoomControls
                escala={render.escala}
                manual={zoom !== null}
                onZoom={(escala) => aplicarZoom(escala)}
              />
            )}
          </div>
        )}
      </nav>
      {liberada && openedOverlay?.page === page && <OverlayDialog bookId={access.asset_id}
        overlay={openedOverlay} onClose={() => setOpenedOverlay(null)} />}
    </div>
  )
}

/** Região de zoom desenhada pelo editor (US-06), em coordenada normalizada.
 *
 * Nasce de `metadata.zoom_regions`; sem regiões o fallback é o duplo clique no
 * wrapper. É `button` de verdade — uma área clicável em `div` ficaria fora do
 * Tab, e mapa e tabela densos são justamente o que o aluno com baixa visão
 * precisa alcançar pelo teclado.
 */
function ZoomRegionButton({
  regiao,
  indice,
  onAmpliar,
}: {
  regiao: ZoomRegion
  indice: number
  onAmpliar: () => void
}) {
  const estilo = {
    left: `${regiao.rect.x * 100}%`,
    top: `${regiao.rect.y * 100}%`,
    width: `${regiao.rect.w * 100}%`,
    height: `${regiao.rect.h * 100}%`,
  }
  const rotulo = regiao.label ?? `Região ${indice + 1}`

  return (
    <button
      type="button"
      className="pdf-zoom-region"
      style={estilo}
      data-testid={`zoom-region-${indice}`}
      aria-label={`Ampliar: ${rotulo}`}
      onClick={onAmpliar}
    >
      <span className="pdf-zoom-region__rotulo">🔍 {rotulo}</span>
    </button>
  )
}

/** Marcador posicionado por coordenada NORMALIZADA (0..1) — RN-04. */
function OverlayMarker({ overlay, onOpen }: { overlay: Overlay; onOpen: () => void }) {
  const estilo = {
    left: `${overlay.rect.x * 100}%`,
    top: `${overlay.rect.y * 100}%`,
    width: `${overlay.rect.w * 100}%`,
    height: `${overlay.rect.h * 100}%`,
  }
  const rotulo =
    overlay.type === 'exercise'
      ? 'Exercício'
      : overlay.type === 'audio'
        ? 'Ouvir áudio'
        : overlay.type === 'interactive' ? 'Abrir objeto interativo'
          : overlay.type === 'link' ? 'Abrir link' : 'Vídeo disponível em breve'

  return (
    <button
      type="button"
      className={`pdf-overlay pdf-overlay--${overlay.type}`}
      style={estilo}
      data-testid={`overlay-${overlay.id}`}
      aria-label={overlay.title ? `${rotulo}: ${overlay.title}` : rotulo}
      title={overlay.title ? `${rotulo}: ${overlay.title}` : rotulo}
      disabled={overlay.type === 'video'}
      onClick={onOpen}
    >
      <span className="pdf-overlay__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          {overlay.type === 'audio' ? <><path d="M9 18V5l12-2v13M9 8l12-2" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></>
            : overlay.type === 'exercise' ? <><path d="M9 5H5v16h14V5h-4M9 3h6v4H9zM8 14l3 3 5-6" /></>
            : overlay.type === 'link' ? <><path d="M14 3h7v7m0-7L10 14M10 5H4v15h15v-6" /></>
            : <><rect x="3" y="3" width="18" height="18" rx="5" /><path d="m10 8 6 4-6 4z" /></>}
        </svg>
      </span>
    </button>
  )
}
